import { useCallback, useEffect, useState } from 'react';
import { FieldValues, SubmitHandler, useForm } from 'react-hook-form';

import {
  Button,
  Icon,
  Restricted,
  Select,
  Table,
  TableColumn,
} from '@components';
import { User } from 'interfaces/User';
import CostCenterAPICaller from 'services/api/costCenter';

type Props = {
  costCenterName: string;
  costCenterId: string;
  users: User[];
  onUpdate: () => void;
};

export default function CostCenterUsersTable(props: Props) {
  const [usersFromGroup, setUsersFromGroup] = useState<User[]>([]);
  const [users, setUsers] = useState<User[]>(props.users);
  const [isAddingUser, setIsAddingUser] = useState(false);
  const { watch, register, setValue, handleSubmit } = useForm();

  const fetchUsersFromGroup = useCallback(async () => {
    const group = await CostCenterAPICaller.fetch(props.costCenterId);
    setUsersFromGroup(group.users);
  }, [props.costCenterId]);

  useEffect(() => {
    fetchUsersFromGroup();
  }, [fetchUsersFromGroup]);

  useEffect(() => {
    setUsers(
      props.users.filter(
        (user) => !usersFromGroup.find((u) => u.id === user.id)
      )
    );
  }, [usersFromGroup, props.users, setUsers]);

  const onSubmit = (async (data: { userId: string }) => {
    if (!data.userId) return;
    setIsAddingUser(true);
    await CostCenterAPICaller.linkUser(props.costCenterId, data.userId).finally(
      () => setIsAddingUser(false)
    );
    await fetchUsersFromGroup();
    setValue('userId', '');
    setValue('userName', '');
    props.onUpdate();
  }) as SubmitHandler<FieldValues>;

  return (
    <div className="p-s-300">
      <h4 className="mb-s-200 color-primary">
        Usuários do centro de custo - <span>{props.costCenterName}</span>
      </h4>
      <Restricted context="costCenters" resource="update">
        <form autoComplete="off" onSubmit={handleSubmit(onSubmit)}>
          <div className="row justify-between pb-s-200">
            <Select
              options={users.map((user) => ({
                label: user.name,
                value: user.id,
              }))}
              onSelect={(option) => {
                if (!option) return;
                setValue('userName', option.label);
                setValue('userId', option.value);
              }}
              placeholder="Pesquisar usuário"
              form={register('userName')}
              value={watch('userName')}
              className="col-sm-4 mr-s-200"
            />
            <Button
              isLoading={isAddingUser}
              type="submit"
              weight="bold"
              design="outlined"
              prefixes={<Icon>group_add</Icon>}
              className="col-sm-2"
            >
              Adicionar
            </Button>
          </div>
        </form>
      </Restricted>
      <Table data={usersFromGroup} showColumnSelector={false}>
        <TableColumn
          key={`${props.costCenterId}-name`}
          header="Nome"
          fromKey="name"
          container
          ellipsis
          cellAlign="start"
        />
        <TableColumn
          key={`${props.costCenterId}-email`}
          header="Email"
          fromKey="email"
          container
          ellipsis
          cellAlign="start"
        />
        <TableColumn
          key={`${props.costCenterId}-actions`}
          header="Ações"
          disableSorting
          width="6%"
          container
          cellAlign="end"
          fromKey="id"
          render={(userId) => (
            <div className="d-flex">
              <Restricted context="costCenters" resource="update">
                <Button
                  weight="bold"
                  design="transparent"
                  prefixes={<Icon>delete</Icon>}
                  onClick={async () => {
                    await CostCenterAPICaller.unlinkUser(
                      props.costCenterId,
                      userId
                    );
                    props.onUpdate();
                    await fetchUsersFromGroup();
                  }}
                />
              </Restricted>
            </div>
          )}
        />
      </Table>
    </div>
  );
}
